import React from 'react'
import { Check, Smartphone, Building2, Zap, Palette, Shield, Globe } from 'lucide-react'

const benefits = [
  {
    icon: Smartphone,
    title: 'PWA Technology',
    description: 'Install as mobile app. Access your business anywhere, anytime.'
  },
  {
    icon: Building2,
    title: 'Multi-Shop Support',
    description: 'Manage multiple locations from one account. Perfect for growing businesses.'
  },
  {
    icon: Zap,
    title: 'Real-time Updates',
    description: 'Instant notifications and live data sync. Stay connected to your business 24/7.'
  },
  {
    icon: Palette,
    title: 'Customizable',
    description: 'Brand with your shop logo and colors. Make it truly yours.'
  },
  {
    icon: Shield,
    title: 'Secure',
    description: 'Bank-level encryption and data protection. Your data is safe with us.'
  },
  {
    icon: Globe,
    title: 'Mobile-First',
    description: 'Works seamlessly on phones and tablets. Designed for on-the-go management.'
  }
]

export default function WhyLaundryMate() {
  return (
    <section id="why" className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Why Choose
            <span className="gradient-text"> LaundryMate</span>?
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            Built specifically for laundry businesses with features that matter most to you.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="flex items-start gap-4 p-6 bg-white dark:bg-gray-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              {/* Icon */}
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                <benefit.icon className="w-6 h-6 text-white" />
              </div>

              {/* Content */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Comparison Section */}
        <div className="bg-white dark:bg-gray-700 rounded-2xl shadow-xl p-8 md:p-12 transition-colors duration-300">
          <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-900 dark:text-white mb-8">
            LaundryMate vs Traditional Methods
          </h3>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Traditional Way */}
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-700 dark:text-gray-200 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 flex items-center justify-center text-sm">✗</span>
                Traditional Way
              </h4>
              <ul className="space-y-3">
                {[
                  'Paper-based order tracking',
                  'Manual inventory counting',
                  'Spreadsheet management',
                  'No customer engagement',
                  'Limited business insights',
                  'Time-consuming processes'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-600 dark:text-gray-300">
                    <span className="text-red-500 mt-1">✗</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* LaundryMate Way */}
            <div className="space-y-4">
              <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 flex items-center justify-center text-sm">✓</span>
                With LaundryMate
              </h4>
              <ul className="space-y-3">
                {[
                  'Digital order management',
                  'Automated inventory tracking',
                  'Centralized dashboard',
                  'Built-in customer campaigns',
                  'Real-time analytics',
                  'Streamlined operations'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700 dark:text-gray-300 font-medium">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <a
              href="https://app.laundrymate.shop"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
            >
              Get Started Now
            </a>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-4">
              Only P***/month • Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
